import React, { useRef } from 'react';
import Hero from '../components/Hero';
import SEO from '../components/SEO';
import { OccasionGrid, BrandsTicker, InfoTicker } from '../components/Marquees';
import CorporateCategoryGrid, { PersonalCategoryGrid } from '../components/CategoryGrid';
import ProductGrid from '../components/ProductGrid';
import { ApproachSection, SignatureSolutions, StatsBar, Differentiators } from '../components/Sections';
import { FAQSection, Testimonials, CaseStudies } from '../components/FaqTestimonials';
import ContactForm from '../components/ContactForm';
import { CORPORATE_CATEGORIES, PERSONAL_CATEGORIES, PRODUCTS, TOP_SELLING, QUICK_CATEGORIES, FAQS } from '../mock/mock';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const DiwaliBanner = () => (
  <section className="bg-[#2C2416] text-white py-4">
    <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#C9A76B]/25 border border-[#C9A76B]/50 flex items-center justify-center">
          <Flame className="w-4 h-4 text-[#F4D5C5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C9A76B]">Diwali 2026 bookings open</div>
          <div className="text-sm text-white">Bulk pricing from <span className="font-semibold text-[#F4D5C5]">₹899/hamper</span> · Early birds save up to 22%</div>
        </div>
      </div>
      <Link to="/diwali" className="bg-[#F4D5C5] text-[#2C2416] rounded-full px-5 py-2 text-xs uppercase tracking-widest font-medium hover:bg-white transition-colors inline-flex items-center gap-2">
        View Diwali edit <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

const QuickCats = () => (
  <section className="py-12 bg-white border-b border-[#F0E7DA]">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-8">
        <p className="font-serif text-[#7A5C4C] text-lg md:text-xl italic">
          "Pick the moment. We will design the gift."
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-6 lg:gap-10">
        {QUICK_CATEGORIES.map((q) => (
          <Link to={`/category/corporate/${q.slug}`} key={q.slug} className="text-center group block">
            <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-36 lg:h-36 rounded-full overflow-hidden border border-[#EFE3D2] group-hover:border-[#C58B76] transition-colors mx-auto aspect-square">
              <img
                src={q.img}
                alt={`${q.name} - Corporate gifting collection by WrapYourWish`}
                width="144"
                height="144"
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="text-xs lg:text-sm text-[#2C2416] mt-2 font-medium leading-tight max-w-[130px] mx-auto">{q.name}</div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const ClientsWeServe = () => {
  const brands = [
    'Decathlon', 'Frido', 'Momentz', 'Arrow', 'UCB', 'MnS', 'Levis', 'Puma',
    'Adidas', 'USPA', 'Aldo', 'SG', 'Trident', 'Portico', 'Welspun', 'Alexa',
    'JBL', 'Philips', 'Skyline', 'Pegion', 'Borosil', 'Milton', 'Prestige',
    'The Natural Wash(TNW)', 'Bath And Body Works', 'Forest Essentials',
    'Khaadi', 'Cross', 'Nautica', 'Sheaffer', 'SWISS Military', 'NOISE',
    'BOAT', 'Tommy Hilfiger', 'American Tourister', 'Skybags', 'Happilo',
    'Portronics'
  ];

  const items = [...brands, ...brands];

  return (
    <section className="py-16 bg-[#FAF6F1] border-b border-[#E9DFD3]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">Our Premium Brand Partners</h2>
          <p className="text-sm text-[#7A5C4C] mt-1">Co-creation platforms & customization partners</p>
        </div>
        <div className="overflow-hidden relative">
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FAF6F1] to-transparent z-10" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#FAF6F1] to-transparent z-10" />
          <div className="flex gap-10 animate-marquee-reverse w-max">
            {items.map((b, i) => (
              <div key={i} className="flex items-center justify-center h-16 min-w-[160px] bg-white rounded-lg px-6 shadow-sm border border-[#E9DFD3]">
                <span className="font-serif-display text-xl text-[#2C2416] whitespace-nowrap">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'WrapYourWish',
    legalName: 'Wrap Your Wish Gifting Studio',
    url: 'https://wrapyourwish.com',
    logo: 'https://wrapyourwish.com/logo_new.png',
    image: 'https://wrapyourwish.com/logo_new.png',
    description: 'Discover premium corporate gifting in Delhi NCR & Pan-India. Custom employee onboarding kits, client hampers & festive gifts with GST billing.',
    telephone: '+918860553236',
    email: 'reachus@wrapyourwish.com',
    priceRange: '₹₹ - ₹₹₹₹',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rajendra Nagar Industrial Area',
      addressLocality: 'Ghaziabad',
      addressRegion: 'Uttar Pradesh',
      postalCode: '201005',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '28.6811',
      longitude: '77.3486',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Delhi NCR' },
      { '@type': 'City', name: 'Delhi' },
      { '@type': 'City', name: 'Gurgaon' },
      { '@type': 'City', name: 'Noida' },
      { '@type': 'City', name: 'Ghaziabad' },
      { '@type': 'City', name: 'Bangalore' },
      { '@type': 'Country', name: 'India' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  return (
    <main>
      <SEO
        title="Corporate Gifting Companies in India | Onboarding Kits & Hampers | WrapYourWish"
        description="Discover premium corporate gifting in Delhi NCR & Pan-India. Custom employee onboarding kits, client hampers & festive gifts with GST billing. Request a quote."
        keywords="corporate gifting companies in Delhi NCR, corporate gifting companies in India, employee onboarding kit suppliers India, bulk client gift hampers, corporate Diwali gifts for employees, work anniversary welcome kits, wedding return gifts luxury bulk"
        canonicalUrl="https://wrapyourwish.com/"
        schemas={[localBusinessSchema, faqSchema]}
      />
      <Hero />
      <InfoTicker />
      <QuickCats />
      <CorporateCategoryGrid items={CORPORATE_CATEGORIES} />
      <ProductGrid title="Featured Corporate Hampers" subtitle="Curated collections our clients love the most" products={PRODUCTS.slice(0, 8)} ctaHref="/corporate-gifting" />
      <ApproachSection />
      <StatsBar />
      <PersonalCategoryGrid items={PERSONAL_CATEGORIES} />
      <ClientsWeServe />
      <BrandsTicker />
      <Differentiators />
      <Testimonials />
      <CaseStudies />
      <FAQSection />
      <ContactForm />
    </main>
  );
};

export default Home;
